// Import the logo files
import metarouterLogo from '/public/assets/metarouter-logo.svg';
import metarouterMark from '/public/assets/metarouter-mark.svg';
import architectLogo from '/public/assets/architect-logo-small.svg';

// Define font paths - we'll fetch these directly
const fontPaths = {
  regular: '/assets/fonts/IBMPlexSans-Regular.ttf',
  bold: '/assets/fonts/IBMPlexSans-Bold.ttf',
  medium: '/assets/fonts/IBMPlexSans-Medium.ttf',
  semiBold: '/assets/fonts/IBMPlexSans-SemiBold.ttf'
};

// Helper function to fetch a file and convert it to base64
const fileToBase64 = async (url: string): Promise<string> => {
  try {
    // Use the full URL with origin for fetching
    const fullUrl = new URL(url, window.location.origin).href;
    const response = await fetch(fullUrl);
    const arrayBuffer = await response.arrayBuffer();
    
    // Convert ArrayBuffer to base64
    const base64 = btoa(
      new Uint8Array(arrayBuffer)
        .reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    
    return base64;
  } catch (error) {
    console.error(`Error fetching font file: ${error}`);
    throw error;
  }
};

// Export the logo URLs
export const logos = {
  metarouterLogo,
  metarouterMark,
  architectLogo,
};

// Helper function to convert SVG to data URL (for compatibility with pdfMake)
export const svgToDataUrl = (svgUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = error => reject(`Error loading image: ${error}`);
    img.src = svgUrl;
  });
};

// Generic function to get data URL for any asset
const getDataUrl = async (assetPath: string, convert: (path: string) => Promise<string>): Promise<string> => {
  try {
    return await convert(assetPath);
  } catch (error) {
    console.error(`Error converting asset to data URL: ${error}`);
    return '';
  }
};

// Function to get the logo as a data URL
export const getMetarouterLogoDataUrl = () => getDataUrl(metarouterLogo, svgToDataUrl);

// Function to get the mark as a data URL
export const getMetarouterMarkDataUrl = () => getDataUrl(metarouterMark, svgToDataUrl);

// Function to get the mark as a data URL
export const getArchitectLogoDataUrl = () => getDataUrl(architectLogo, svgToDataUrl);

// Font data storage for use in the PDF template
export let fontData: {
  regular: string;
  bold: string;
  medium: string;
  semiBold: string;
} = {
  regular: '',
  bold: '',
  medium: '',
  semiBold: ''
};

// Preload fonts and store as base64
export const preloadFonts = async () => {
  try {
    fontData.regular = await fileToBase64(fontPaths.regular);
    fontData.bold = await fileToBase64(fontPaths.bold);
    fontData.medium = await fileToBase64(fontPaths.medium);
    fontData.semiBold = await fileToBase64(fontPaths.semiBold);
    return true;
  } catch (error) {
    console.error('Failed to preload fonts:', error);
    return false;
  }
};

// Export the font paths for reference
export { fontPaths };
